import { Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import { AuthService } from '../auth/auth.service';
import { EventClosingService } from '../event-closing/event-closing.service';
import { isCourtesyOrder } from 'src/app/shared/courtesy/courtesy.model';

/**
 * Día operativo de un evento. Un evento de varios días vende hasta la
 * madrugada, así que el "día" no cambia a medianoche sino a las
 * DAY_CUTOFF_HOUR: una orden de las 01:30 pertenece a la jornada que empezó
 * la tarde anterior. Con ese día se cierran las cajas de la jornada y se
 * agrupan las estadísticas del cliente.
 */
export const DAY_CUTOFF_HOUR = 6;

/** Marca que deja el cierre del día en cada orden. */
export interface DayClosingMark {
    /** Día operativo al que pertenece la orden (YYYY-MM-DD). */
    closing_day: string;
    day_closed: boolean;
    day_closing_id: string;
    day_closed_at: any;
    day_closed_by: string;
}

/** Totales de un día operativo (o de una caja dentro de un día). */
export interface DayTotals {
    orders: number;
    total: number;
    cash: number;
    card: number;
    pos: number;
    other: number;
    cost_admin: number;
    /** Regalías (órdenes con total 0) del día; van dentro de `orders`. */
    courtesy: number;
}

export interface DayBoxTotals extends DayTotals {
    box_key: string;
    box_name: string;
}

export interface DayGroup extends DayTotals {
    /** YYYY-MM-DD del día operativo. */
    day: string;
    /** "Hoy", "Ayer" o el día de la semana, más la fecha. */
    label: string;
    /** Órdenes vivas (no canceladas) del día. */
    list: any[];
    /** Órdenes del día todavía sin cierre (canceladas incluidas: también se marcan). */
    open_count: number;
    /** true si el día no tiene ninguna orden sin cerrar. */
    closed: boolean;
    boxes: DayBoxTotals[];
}

export type DayClosingScope = 'until_yesterday' | 'all';

/** Constancia de un cierre del día: qué días abarcó y cuánto sumó cada uno. */
export interface DayClosingRun {
    run_id: string;
    event_key: string;
    event_name: string;
    countryCode: string;
    scope: DayClosingScope;
    closed_at: any;
    closed_by: string;
    orders_count: number;
    total: number;
    days: { day: string; label: string; orders: number; total: number; cash: number; card: number; pos: number; boxes: DayBoxTotals[] }[];
}

const WEEKDAYS = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

@Injectable({
    providedIn: 'root'
})
export class EventDayService {

    private readonly BATCH_SIZE = 400;

    constructor(
        private api: ApiService,
        private auth: AuthService,
        private eventClosing: EventClosingService,
    ) { }

    // ═══════════════════════════════════════════════════════════════════════
    //  DÍA OPERATIVO (funciones puras)
    // ═══════════════════════════════════════════════════════════════════════

    static pad(n: number): string {
        return n < 10 ? '0' + n : String(n);
    }

    /** Fecha de una orden: Timestamp de Firestore, Date o texto. */
    static orderDate(order: any): Date | null {
        const raw = order ? order.added_date : null;
        if (!raw) return null;
        if (raw.toDate) return raw.toDate();
        if (raw instanceof Date) return raw;
        const parsed = new Date(raw);
        return isNaN(parsed.getTime()) ? null : parsed;
    }

    /** YYYY-MM-DD del día operativo al que pertenece un instante (hora local). */
    static operatingDay(date: Date): string {
        const shifted = new Date(date.getTime() - DAY_CUTOFF_HOUR * 3600 * 1000);
        return `${shifted.getFullYear()}-${EventDayService.pad(shifted.getMonth() + 1)}-${EventDayService.pad(shifted.getDate())}`;
    }

    static orderDay(order: any): string {
        // Una orden ya cerrada conserva el día con el que se cerró.
        if (order && order.closing_day) return String(order.closing_day);
        const date = EventDayService.orderDate(order);
        return date ? EventDayService.operatingDay(date) : 'sin-fecha';
    }

    static today(now: Date = new Date()): string {
        return EventDayService.operatingDay(now);
    }

    static isDayClosed(order: any): boolean {
        return !!order && order.day_closed === true;
    }

    /** "Hoy · jue 12 sep 2026", "Ayer · …", "mié 10 sep 2026". */
    static dayLabel(day: string, now: Date = new Date()): string {
        if (!day || day === 'sin-fecha') return 'Sin fecha';
        const parts = day.split('-').map(Number);
        const date = new Date(parts[0], parts[1] - 1, parts[2]);
        const text = `${WEEKDAYS[date.getDay()]} ${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
        const today = EventDayService.today(now);
        if (day === today) return `Hoy · ${text}`;
        const yesterday = EventDayService.operatingDay(new Date(now.getTime() - 24 * 3600 * 1000));
        if (day === yesterday) return `Ayer · ${text}`;
        return text;
    }

    // ── Rango de fechas ──────────────────────────────────────────────────

    /** Días YYYY-MM-DD ordenados de más antiguo a más nuevo; el orden natural de las cadenas sirve. */
    static normalizeRange(from: string, to: string): { from: string; to: string } {
        const a = String(from || ''), b = String(to || '');
        if (!a || !b) return { from: a || b, to: b || a };
        return a <= b ? { from: a, to: b } : { from: b, to: a };
    }

    /** Jornadas dentro del rango (ambos extremos incluidos). */
    static groupsInRange(groups: DayGroup[], from: string, to: string): DayGroup[] {
        const r = EventDayService.normalizeRange(from, to);
        if (!r.from || !r.to) return [];
        return (groups || []).filter(g => g.day !== 'sin-fecha' && g.day >= r.from && g.day <= r.to);
    }

    /** Órdenes vivas de las jornadas dentro del rango. */
    static ordersInRange(groups: DayGroup[], from: string, to: string): any[] {
        return EventDayService.groupsInRange(groups, from, to).reduce((acc, g) => acc.concat(g.list), [] as any[]);
    }

    /** "dd/mm/aaaa" a partir de YYYY-MM-DD. */
    static shortDay(day: string): string {
        const parts = String(day || '').split('-');
        return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : String(day || '');
    }

    /** "Del 10/09/2026 al 12/09/2026" o "10/09/2026" si es un solo día. */
    static rangeLabel(from: string, to: string): string {
        const r = EventDayService.normalizeRange(from, to);
        if (!r.from || !r.to) return '';
        if (r.from === r.to) return EventDayService.shortDay(r.from);
        return `Del ${EventDayService.shortDay(r.from)} al ${EventDayService.shortDay(r.to)}`;
    }

    /** Día operativo `days` días antes de hoy (0 = hoy). */
    static daysAgo(days: number, now: Date = new Date()): string {
        return EventDayService.operatingDay(new Date(now.getTime() - days * 24 * 3600 * 1000));
    }

    private static emptyTotals(): DayTotals {
        return { orders: 0, total: 0, cash: 0, card: 0, pos: 0, other: 0, cost_admin: 0, courtesy: 0 };
    }

    private static round(n: number): number {
        return Math.round(n * 100) / 100;
    }

    private static addOrder(bucket: DayTotals, order: any) {
        const amount = Number(order.total || 0);
        bucket.orders += 1;
        if (isCourtesyOrder(order)) bucket.courtesy += 1;
        bucket.total = EventDayService.round(bucket.total + amount);
        bucket.cost_admin = EventDayService.round(bucket.cost_admin + Number(order.cost_admin || 0));
        const type = order.payment && order.payment.type ? order.payment.type : '';
        if (type === 'cash') bucket.cash = EventDayService.round(bucket.cash + amount);
        else if (type === 'card') bucket.card = EventDayService.round(bucket.card + amount);
        else if (type === 'pos') bucket.pos = EventDayService.round(bucket.pos + amount);
        else bucket.other = EventDayService.round(bucket.other + amount);
    }

    /**
     * Agrupa órdenes por día operativo, de la más reciente a la más antigua,
     * con totales por método de pago y por caja. Las canceladas no suman pero
     * sí cuentan como "sin cerrar" para que el cierre las marque también.
     */
    static groupByDay(orders: any[], now: Date = new Date()): DayGroup[] {
        const map: { [day: string]: DayGroup } = {};
        const boxes: { [day: string]: { [box: string]: DayBoxTotals } } = {};

        (orders || []).forEach(order => {
            if (!order || order.deleted === true) return;
            const day = EventDayService.orderDay(order);
            if (!map[day]) {
                map[day] = { ...EventDayService.emptyTotals(), day, label: EventDayService.dayLabel(day, now), list: [], open_count: 0, closed: true, boxes: [] };
                boxes[day] = {};
            }
            const group = map[day];
            if (!EventDayService.isDayClosed(order)) {
                group.open_count += 1;
                group.closed = false;
            }
            if (order.status === 'canceled' || order.status === 'cancelled') return;
            group.list.push(order);
            EventDayService.addOrder(group, order);
            const boxKey = order.event_category_key || 'sin_caja';
            if (!boxes[day][boxKey]) boxes[day][boxKey] = { ...EventDayService.emptyTotals(), box_key: boxKey, box_name: '' };
            EventDayService.addOrder(boxes[day][boxKey], order);
        });

        return Object.keys(map).sort().reverse().map(day => {
            const group = map[day];
            group.boxes = Object.keys(boxes[day]).map(k => boxes[day][k]).sort((a, b) => b.total - a.total);
            return group;
        });
    }

    /** Órdenes que entran en un cierre según el alcance elegido. */
    static selectForClosing(orders: any[], scope: DayClosingScope, now: Date = new Date()): any[] {
        const today = EventDayService.today(now);
        return (orders || []).filter(order => {
            if (!order || order.deleted === true || EventDayService.isDayClosed(order)) return false;
            if (scope === 'all') return true;
            const day = EventDayService.orderDay(order);
            return day !== 'sin-fecha' && day < today;
        });
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  FIRESTORE
    // ═══════════════════════════════════════════════════════════════════════

    /** Todas las órdenes vivas del evento que todavía no pasaron por un cierre del día. */
    async getPendingOrders(eventKey: string): Promise<any[]> {
        const all = await this.eventClosing.getEventOrders(eventKey);
        return all.filter(order => !EventDayService.isDayClosed(order));
    }

    /** Nombres de las cajas (zonas) para el resumen; las zonas viven en bricks. */
    async resolveBoxNames(groups: DayGroup[]): Promise<void> {
        const names: { [key: string]: string } = {};
        const keys: string[] = [];
        groups.forEach(g => g.boxes.forEach(b => { if (keys.indexOf(b.box_key) < 0) keys.push(b.box_key); }));
        await Promise.all(keys.map(async key => {
            if (!key || key === 'sin_caja') { names[key] = 'Sin zona asignada'; return; }
            try {
                const doc: any = await this.api.getDocument('bricks', key);
                names[key] = doc && doc.name ? doc.name : key;
            } catch (e) {
                names[key] = key;
            }
        }));
        groups.forEach(g => g.boxes.forEach(b => b.box_name = names[b.box_key] || b.box_key));
    }

    /**
     * Cierra el día: marca cada orden con su día operativo y el id del cierre
     * (en lotes), y deja una constancia en `event_day_closings` con los
     * totales por día y por caja. Devuelve la constancia.
     */
    async closeDays(eventKey: string, eventName: string, orders: any[], scope: DayClosingScope): Promise<DayClosingRun> {
        const now = new Date();
        const by = this.auth.email || '';
        const runId = `${this.auth.countryCode}_${eventKey}_${now.getTime()}`;
        const firestore = this.api.db.firestore;

        for (let i = 0; i < orders.length; i += this.BATCH_SIZE) {
            const batch = firestore.batch();
            orders.slice(i, i + this.BATCH_SIZE).forEach(order => {
                const mark: DayClosingMark = {
                    closing_day: EventDayService.orderDay(order),
                    day_closed: true,
                    day_closing_id: runId,
                    day_closed_at: now,
                    day_closed_by: by,
                };
                batch.update(firestore.collection('orders').doc(order.$key), mark);
            });
            await batch.commit();
        }

        const groups = EventDayService.groupByDay(orders.map(o => ({ ...o, day_closed: true, closing_day: EventDayService.orderDay(o) })), now);
        await this.resolveBoxNames(groups);
        const run: DayClosingRun = {
            run_id: runId,
            event_key: eventKey,
            event_name: eventName || '',
            countryCode: this.auth.countryCode,
            scope,
            closed_at: now,
            closed_by: by,
            orders_count: orders.length,
            total: EventDayService.round(groups.reduce((acc, g) => acc + g.total, 0)),
            days: groups.map(g => ({
                day: g.day, label: g.label, orders: g.orders, total: g.total, cash: g.cash, card: g.card, pos: g.pos,
                boxes: g.boxes.map(b => ({ ...b })),
            })),
        };
        // En cola: la constancia es informativa; las órdenes ya quedaron marcadas.
        this.api.setDocumentQueued('event_day_closings', runId, run);
        return run;
    }

    /** Cierres del día ya hechos en el evento, del más reciente al más antiguo. */
    async getClosingRuns(eventKey: string): Promise<DayClosingRun[]> {
        if (!eventKey) return [];
        try {
            const snap = await this.api.getRef('event_day_closings').ref
                .where('countryCode', '==', this.auth.countryCode)
                .where('event_key', '==', eventKey)
                .get();
            const list: DayClosingRun[] = [];
            snap.forEach(doc => list.push(doc.data() as DayClosingRun));
            const time = (run: DayClosingRun) => {
                const raw: any = run.closed_at;
                return raw && raw.toDate ? raw.toDate().getTime() : new Date(raw).getTime();
            };
            return list.sort((a, b) => time(b) - time(a));
        } catch (e) {
            console.warn('No se pudieron leer los cierres del día:', e);
            return [];
        }
    }
}
